import React from 'react';
import { useTranslation } from 'react-i18next';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

import { Card } from 'components/Card';
import { Section, Props as SectionProps } from 'components/Section';
import { useChain, useSettings } from 'contexts';
import { formatCount, formatPercent, formatUsd } from 'utils/number';
import { Currency, CurrencyBalance } from 'utils/types';

type Props = SectionProps & {
  assetsTitle: string;
  balance: CurrencyBalance;
  balanceUsd: CurrencyBalance;
  balanceTotalUsd: number;
};

const WalletBreakdownSection: React.FC<Props> = ({
  title,
  icon,
  assetsTitle,
  balance,
  balanceUsd,
  balanceTotalUsd,
}) => {
  const { t } = useTranslation();
  const { styleSlug } = useChain();
  const { isDarkMode } = useSettings();

  const currencies = Object.keys(balanceUsd) as Array<Currency>;

  return (
    <Section className="space-y-4" title={title} icon={icon}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-4">
        <Card
          title={assetsTitle}
          body={
            <>
              <HighchartsReact
                highcharts={Highcharts}
                options={
                  {
                    credits: false,
                    chart: {
                      type: 'pie',
                      backgroundColor: 'transparent',
                    },
                    title: {
                      text: '',
                    },
                    tooltip: {
                      pointFormatter: function () {
                        return formatUsd(this.y || 0);
                      },
                    },
                    plotOptions: {
                      pie: {
                        colors: ['#17629a', '#00db34', '#FFAB2D', '#4AA9A5', '#8147E5', '#E0980F'],
                        cursor: 'pointer',
                        dataLabels: {
                          enabled: true,
                          format: '<b>{point.name}</b>: {point.percentage:.2f}%',
                          color: isDarkMode ? '#fff' : '#000',
                        },
                      },
                    },
                    series: [
                      {
                        type: 'pie',
                        data: currencies
                          .filter((currency) => balanceUsd[currency])
                          .sort((a, b) => balanceUsd[b]! - balanceUsd[a]!)
                          .map((currency) => ({
                            name: currency,
                            y: balanceUsd[currency],
                          })),
                      },
                    ],
                  } as Highcharts.Options
                }
              />
            </>
          }
        />
        <Card
          className="pb-4"
          body={
            <table className="w-full table-fixed text-lg text-left">
              <thead className={`uppercase text-${styleSlug}-primary dark:text-${styleSlug}-light`}>
                <tr>
                  <th className="pb-2 font-normal">{t('portfolio.wallet_breakdown.token')}</th>
                  <th className="pb-2 font-normal text-right">
                    {t('portfolio.wallet_breakdown.balance')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {currencies.map((currency, i) => (
                  <tr
                    key={currency}
                    className={
                      i !== currencies.length - 1
                        ? 'border-b border-gray-e6e6e6 dark:border-gray-444'
                        : ''
                    }
                  >
                    <td className="py-2">
                      <span className="block text-gray-444 dark:text-white">{currency}</span>
                      <span className="block text-gray-999">
                        {formatCount(balance[currency]!, 2)}
                      </span>
                    </td>
                    <td className="py-2 text-right">
                      {formatUsd(balanceUsd[currency]!)}{' '}
                      <span className="text-gray-999">
                        ({formatPercent(balanceUsd[currency]! / balanceTotalUsd)})
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          }
        />
      </div>
    </Section>
  );
};

export default WalletBreakdownSection;
